// ============================================================================
// Equipment detail — /fleet/equipment/:machineId
//
// This is the route a QR code resolves to, and the route the rollup links to.
// Before this file existed there was NO route for a floor machine at all: the
// 2026-07-17 Fleet build gave them a table row and an edit modal and nothing
// else, so a scanned tag had nowhere to land.
//
// SCAN MODE (?src=qr) leads with the confirm question, because when you have
// just walked up to a machine the recorded location is the thing in doubt.
// IN-APP MODE leads with the two actions, because Jack trusts the record when
// he is sitting at a desk and asking him "is it still there?" wastes a tap.
// Ben's call, and the synthesis of his disagreement with Jack: the confirm is
// ONE TAP on a screen that had to render anyway, never a separate step.
//
// Deliberately NOT here: a diagnostics checklist, a photo upload, parts
// ordering, maintenance scheduling. The MVP is BROKEN or MOVED.
// ============================================================================
import { canRenumberMachine } from '../../shared/role-gates';
import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Building2, Check, Hash, MapPin, Warehouse } from 'lucide-react';
import { EmptyState, ErrorState, PageHeader, Skeleton, useToast } from '../../shared/components';
import { errorMessage } from '../../shared/errors';
import { useAuth } from '../../shared/auth';
import { fmtDate, machineLocation } from './hooks/use-fleet-data';
import {
  confirmMachineLocation,
  useEquipmentDetail,
  useLocationNames,
  type MovementEventRow,
} from './hooks/use-asset-location';
import { chipClasses } from './compliance';
import { confirmedAgo, isStale, statusLabel, statusTone } from './machine-status';
import {
  MachineActions,
  MoveMachineSheet,
  MachineStatusSheet,
  RenumberMachineSheet,
} from './machine-actions';

export default function EquipmentDetail() {
  const { machineId } = useParams<{ machineId: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { success, error } = useToast();

  const isScan = params.get('src') === 'qr';
  const { machine, events, nameMap, loading, error: loadError, notFound, refetch } =
    useEquipmentDetail(machineId);
  // A RESOLVER, and only for the history timeline — it names places a machine
  // has BEEN. The machine's own location comes from the view, below.
  const { byId } = useLocationNames();

  const [answered, setAnswered] = useState<'yes' | 'no' | null>(null);
  const [sheet, setSheet] = useState<'move' | 'status' | 'renumber' | null>(null);
  const [busy, setBusy] = useState(false);

  // The renumber affordance is leadership-only, matching the RPC. The database
  // is the authority — hiding the button is politeness, not the gate.
  const { profile } = useAuth();
  const mayRenumber = canRenumberMachine(profile);

  const where = useMemo(() => machineLocation(machine), [machine]);

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl space-y-3 p-4 md:p-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-28 w-full" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-3xl p-4 md:p-6">
        <ErrorState body={loadError} onRetry={refetch} />
      </div>
    );
  }

  // 74 pre-printed tags (FM-0027…FM-0100) will resolve to nothing for months.
  // That is a normal outcome, not an error, and it gets its own words.
  //
  // ⛔ THERE IS NO CLAIM FORM HERE AND THERE SHOULD NOT BE. Ben's ruling,
  // 2026-08-27: a tag is never affixed to a machine that is not already in the
  // system. Tags print once for FM-0001…FM-0100; the ones whose machines exist
  // go out now, the rest wait in a drawer in number order. A new machine is
  // added in the app, the system gives it the next id, and only THEN does its
  // tag leave the drawer. So by the time a tag is scanned in the field its id
  // already has a machine — and the alignment holds because the ids are
  // contiguous, `block_delete` makes a hole impossible, and
  // rpc_asset_save_machine is the only thing that mints an FM id.
  //
  // A person seeing this screen is therefore in one of two situations, and
  // both are answered by saying plainly which id the tag carries and that
  // nothing is assigned to it yet: they are holding a drawer tag, or a tag was
  // affixed out of order and the record needs creating.
  if (notFound || !machine) {
    return (
      <div className="mx-auto max-w-3xl p-4 md:p-6">
        <EmptyState
          title={`This tag is ${machineId}`}
          body={`No machine has been assigned to ${machineId} yet, so this tag will not work until one is added in the TCS system. Tags are issued in number order as new machines are added — if this tag is already stuck to a machine, tell the National Accounts Manager so the machine can be added.`}
        />
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={() => navigate('/fleet')}
            className="rounded-md border border-tcs-border bg-tcs-raised px-3 py-2 text-sm text-tcs-text hover:bg-tcs-surface"
          >
            Go to Fleet &amp; Assets
          </button>
        </div>
      </div>
    );
  }

  const title = [machine.machine_brand, machine.machine_model].filter(Boolean).join(' ') || machine.machine_id;
  const stale = isStale(machine.last_confirmed_at);

  async function confirmHere() {
    setBusy(true);
    try {
      const msg = await confirmMachineLocation(machine!.machine_id, isScan ? 'qr_scan' : 'app');
      success(msg);
      setAnswered('yes');
      refetch();
    } catch (e) {
      error(errorMessage(e, 'Could not record the confirmation.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4 md:p-6">
      <button
        type="button"
        onClick={() => navigate('/fleet')}
        className="inline-flex items-center gap-1.5 text-sm text-tcs-text-muted hover:text-tcs-text"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Fleet &amp; Assets
      </button>

      <PageHeader
        eyebrow="Floor machine"
        title={title}
        meta={`${machine.machine_id} · ${machine.serial_number ?? 'no serial on record'}`}
      />

      {/* ---- identity + location ------------------------------------------ */}
      <div className="rounded-xl border border-tcs-border bg-tcs-raised p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium ${chipClasses(statusTone(machine.status))}`}>
            {statusLabel(machine.status)}
          </span>
          {stale && (
            <span className="inline-flex rounded-full border border-st-warn-border bg-st-warn-bg px-2 py-0.5 text-[11px] font-medium text-st-warn-fg-strong">
              Location unconfirmed
            </span>
          )}
        </div>

        <dl className="mt-3 divide-y divide-tcs-border">
          <Row label="Recorded location">
            {where.kind === 'unassigned' ? (
              // Visually distinct but not alarming: no location recorded is a
              // gap to fill, not a fault. Track reality, surface gaps.
              <span className="text-tcs-text-muted">Unassigned</span>
            ) : (
              <span className="inline-flex items-center gap-1.5">
                {where.kind === 'office' ? (
                  <Warehouse className="h-3.5 w-3.5 text-tcs-text-muted" />
                ) : (
                  <Building2 className="h-3.5 w-3.5 text-tcs-text-muted" />
                )}
                {/* location_display, never location_id. The id is a tooltip. */}
                <span title={where.id ?? undefined}>{where.display}</span>
              </span>
            )}
          </Row>
          <Row label="Last confirmed">
            {confirmedAgo(machine.last_confirmed_at)}
            {machine.last_confirmed_by_eid && (
              <span className="text-tcs-text-muted">
                {' '}
                · {nameMap.get(machine.last_confirmed_by_eid) ?? machine.last_confirmed_by_eid}
              </span>
            )}
          </Row>
          <Row label="Owner">
            {machine.assigned_employee_eid
              ? nameMap.get(machine.assigned_employee_eid) ?? machine.assigned_employee_eid
              : 'unassigned'}
          </Row>
          <Row label="Warranty">{fmtDate(machine.warranty_exp)}</Row>
          {machine.notes && <Row label="Notes">{machine.notes}</Row>}
        </dl>
      </div>

      {/* ---- the decision -------------------------------------------------- */}
      {isScan && answered === null ? (
        <div className="rounded-xl border border-tcs-border bg-tcs-bg p-4">
          <h2 className="text-[17px] font-semibold leading-snug text-tcs-text">
            {where.kind !== 'unassigned'
              ? `Is this machine still at ${where.display}?`
              : 'Where is this machine?'}
          </h2>
          <p className="mt-1 text-[12.5px] text-tcs-text-muted">
            {where.kind !== 'unassigned'
              ? 'One tap either way — answering “no” is exactly as quick.'
              : 'No location is recorded for this machine yet.'}
          </p>
          {where.kind !== 'unassigned' ? (
            <div className="mt-3 grid grid-cols-2 gap-2.5">
              <button
                type="button"
                onClick={confirmHere}
                disabled={busy}
                className="flex min-h-[3.5rem] items-center justify-center gap-2 rounded-xl bg-st-ok-solid px-3 text-base font-semibold text-st-solid-ink disabled:opacity-60"
              >
                <Check className="h-4 w-4" />
                {busy ? 'Saving…' : 'Yes — still here'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setAnswered('no');
                  setSheet('move');
                }}
                className="flex min-h-[3.5rem] items-center justify-center gap-2 rounded-xl border border-tcs-border-input bg-tcs-raised px-3 text-base font-semibold text-tcs-text"
              >
                <MapPin className="h-4 w-4" />
                No — it moved
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setSheet('move')}
              className="mt-3 flex min-h-[3.5rem] w-full items-center justify-center gap-2 rounded-xl bg-tcs-inverse px-3 text-base font-semibold text-tcs-inverse-ink"
            >
              <MapPin className="h-4 w-4" />
              Set its location
            </button>
          )}
          <button
            type="button"
            onClick={() => setAnswered('no')}
            className="mt-2.5 w-full rounded-xl border border-tcs-border bg-tcs-raised py-2.5 text-sm text-tcs-text-secondary"
          >
            Not sure — skip
          </button>
        </div>
      ) : (
        <div className="rounded-xl border border-tcs-border bg-tcs-bg p-4">
          {isScan && answered === 'yes' && (
            <div className="mb-3 rounded-lg border border-st-ok-border bg-st-ok-bg px-3 py-2.5">
              <p className="text-sm font-semibold text-st-ok-fg-strong">
                Confirmed — thank you.
              </p>
              <p className="text-[12.5px] text-st-ok-fg">
                Recorded just now. Nothing else is needed.
              </p>
            </div>
          )}
          <h2 className="mb-2.5 text-[15px] font-semibold text-tcs-text">
            {isScan && answered === 'yes' ? 'Anything else?' : 'Update this machine'}
          </h2>
          <MachineActions
            machine={machine}
            source={isScan ? 'qr_scan' : 'app'}
            showConfirm={!isScan}
            onDone={refetch}
          />
          {mayRenumber && (
            // Deliberately quiet and last. Minting fills the lowest unused
            // number on its own; this is only for the case where a printed tag
            // is already on the machine and the record has to move to match it.
            <button
              type="button"
              onClick={() => setSheet('renumber')}
              className="mt-2.5 inline-flex items-center gap-1.5 text-[12.5px] text-tcs-text-muted hover:text-tcs-text"
            >
              <Hash className="h-3.5 w-3.5" />
              Renumber this machine
            </button>
          )}
        </div>
      )}

      {/* ---- history ------------------------------------------------------- */}
      <div className="rounded-xl border border-tcs-border bg-tcs-raised">
        <h2 className="border-b border-tcs-border px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-tcs-text-muted">
          History
        </h2>
        {events.length === 0 ? (
          <p className="px-4 py-6 text-sm text-tcs-text-muted">
            Nothing recorded yet. Confirming or moving this machine starts its history.
          </p>
        ) : (
          <ul className="divide-y divide-tcs-border">
            {events.map((e) => (
              <li key={e.event_id} className="px-4 py-2.5">
                <p className="text-sm text-tcs-text">{describeEvent(e, byId)}</p>
                <p className="mt-0.5 text-[11.5px] text-tcs-text-muted">
                  {fmtDate(e.effective_at)}
                  {' · '}
                  {e.actor_eid ? nameMap.get(e.actor_eid) ?? e.actor_eid : 'no name recorded'}
                  {' · '}
                  {sourceLabel(e.source)}
                  {e.note ? ` · “${e.note}”` : ''}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>

      {sheet === 'move' && (
        <MoveMachineSheet
          machine={machine}
          source={isScan ? 'qr_scan' : 'app'}
          onClose={() => setSheet(null)}
          onSaved={() => {
            setAnswered('no');
            refetch();
          }}
        />
      )}
      {sheet === 'status' && (
        <MachineStatusSheet
          machine={machine}
          source={isScan ? 'qr_scan' : 'app'}
          onClose={() => setSheet(null)}
          onSaved={refetch}
        />
      )}
      {sheet === 'renumber' && (
        <RenumberMachineSheet
          machine={machine}
          onClose={() => setSheet(null)}
          // The id in the URL is now the OLD one, so the page has to go to the
          // new card rather than refetch a machine that no longer answers to it.
          onSaved={() => navigate('/fleet')}
        />
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-2 text-sm">
      <dt className="text-tcs-text-muted">{label}</dt>
      <dd className="text-right font-medium text-tcs-text">{children}</dd>
    </div>
  );
}

function place(
  facilityId: string | null,
  officeId: string | null,
  byId: Map<string, { name: string }>,
): string | null {
  const id = facilityId ?? officeId;
  if (!id) return null;
  return byId.get(id)?.name ?? id;
}

function describeEvent(e: MovementEventRow, byId: Map<string, { name: string }>): string {
  const from = place(e.from_facility_id, e.from_office_id, byId);
  const to = place(e.to_facility_id, e.to_office_id, byId);
  switch (e.event_type) {
    case 'confirmed_in_place':
      return from ? `Confirmed at ${from}` : 'Confirmed in place';
    case 'moved':
      return from ? `Moved from ${from} to ${to}` : `Location set to ${to}`;
    case 'corrected':
      // A renumber is a correction with no destination — nothing moved, the
      // number changed. Without this guard it rendered "… actually null".
      // The old-to-new mapping and the reason are in the note beneath.
      if (!to) return 'Record corrected';
      return `Record corrected — ${from ?? 'unrecorded'} was wrong, actually ${to}`;
    case 'status_changed':
      return `${statusLabel(e.from_status)} → ${statusLabel(e.to_status)}`;
    case 'claimed':
      return `Tag registered to this machine${to ? ` at ${to}` : ''}`;
    case 'imported':
      return 'Imported from the original asset list';
    default:
      return e.event_type;
  }
}

function sourceLabel(source: string): string {
  switch (source) {
    case 'qr_scan':
      return 'QR scan';
    case 'app':
      return 'in app';
    case 'chat':
      return 'chat';
    case 'voice':
      return 'voice';
    case 'import':
      return 'import';
    case 'admin':
      return 'admin';
    default:
      return source;
  }
}
